import React, { useContext } from 'react';
import cx from 'classnames';
import { makeStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';

import { Badge, Kind as BadgeKind, Typography, units } from '@ha/dsl';

import { useIntl } from 'ha/i18n';
import { useLocation } from 'ha/helpers/router';
import FilterLayout from 'ha/components/Filters/FilterLayout';
import { DEFAULT_CURRENCY } from 'ha/constants/Currencies';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import { SearchFiltersContext } from 'ha/pages/Search/SearchFiltersContext';
import { SearchAreaValue } from 'ha/types/SearchFilters';

import { getNumberOfListings } from '../../selectors';
import { getIsSearchAreaFilterHidden } from '../../selectors/filterSelectors';
import SearchFilters from './SearchFilters';
import SearchFiltersTags from './SearchFiltersTags';
import getFilterTagsCount from '../../utils/getFilterTagsCount';

const useStyles = makeStyles(theme => ({
  wrapper: {
    marginLeft: units.rem(1.5),
    marginRight: units.rem(1.5),
  },
  badgeContainer: {
    paddingBottom: units.rem(1.5),
  },
  badge: {
    backgroundColor: theme.palette.orange.main,
    marginLeft: units.rem(0.5),
  },
}));

interface Props {
  onShowResultsClick?: () => void;
  withDateFilter?: boolean;
}

const SearchFiltersButtonContent: React.FC<Props> = ({
  onShowResultsClick,
  withDateFilter,
}) => {
  const { T } = useIntl();
  const classes = useStyles();
  const location = useLocation();
  const { md } = useMediaQuery();

  const isSearchAreaFilterHidden = useSelector(getIsSearchAreaFilterHidden);

  const {
    localState,
    onChangeBills,
    onChangeCurrency,
    onChangeDates,
    onChangeFurniture,
    onChangeGender,
    onChangePrice,
    onChangeRecentlyAdded,
    onChangeRegistration,
    onChangeRooms,
    onChangeRules,
    onChangeSuitableFor,
    onChangeTypes,
    onChangeSearchAreaFilter,
    onChangePropertySize,
    onChangeFacilities,
    onChangeAmenities,
  } = useContext(SearchFiltersContext);

  const tagsState = React.useMemo(
    () => ({
      ...localState,
      dates: withDateFilter
        ? localState.dates
        : { startDate: null, endDate: null },
      price: localState.price,
      searchArea: isSearchAreaFilterHidden ? [] : localState.searchArea,
    }),
    [localState, withDateFilter],
  );

  const tagsCount = getFilterTagsCount(tagsState);

  const total = useSelector(getNumberOfListings);

  const handleShowResultsClick = React.useCallback(() => {
    if (onShowResultsClick) {
      onShowResultsClick();
    }
  }, [onShowResultsClick]);

  const handleResetClick = React.useCallback(() => {
    if (withDateFilter) {
      onChangeDates({ startDate: null, endDate: null });
    }

    onChangePrice({ min: null, max: null });
    onChangeCurrency(DEFAULT_CURRENCY);

    onChangeBills([]);
    onChangeFurniture([]);
    onChangeGender([]);
    onChangeRecentlyAdded(null);
    onChangeRegistration([]);
    onChangeRooms({ bedroomCount: [] });
    onChangeRules([]);
    onChangeSuitableFor([]);
    onChangeTypes([]);
    onChangePropertySize({});
    onChangeFacilities([]);
    onChangeAmenities([]);

    if (!isSearchAreaFilterHidden) {
      onChangeSearchAreaFilter([SearchAreaValue.CityBounds]);
    }
  }, [
    withDateFilter,
    onChangeBills,
    onChangeCurrency,
    onChangeDates,
    onChangeFurniture,
    onChangeGender,
    onChangePrice,
    onChangeRecentlyAdded,
    onChangeRegistration,
    onChangeRooms,
    onChangeRules,
    onChangeSuitableFor,
    onChangeTypes,
    onChangeSearchAreaFilter,
    onChangePropertySize,
    onChangeFacilities,
    onChangeAmenities,
    isSearchAreaFilterHidden,
  ]);

  return (
    <FilterLayout
      type={md ? 'desktop' : 'mobile'}
      showResultsButtonText={`${total} ${T('Results')}`}
      onShowResultsClick={handleShowResultsClick}
      resetButtonText={T('cta.clear_all')}
      updateSearchAlert={!md && !!location.query?.updateSearchAlert}
      updateSearchAlertText={T('search_alert_update_button')}
      onResetClick={handleResetClick}
      title={
        !md ? (
          <Badge
            kind={BadgeKind.inline}
            color="secondary"
            className={classes.wrapper}
            badgeClassName={classes.badge}
            badgeContent={tagsCount}
          >
            <Typography.H6>{T('Filters')}</Typography.H6>
          </Badge>
        ) : undefined
      }
    >
      {tagsCount > 0 && (
        <div className={cx(classes.wrapper, classes.badgeContainer)}>
          <SearchFiltersTags />
        </div>
      )}
      <SearchFilters withDateFilter={withDateFilter} />
    </FilterLayout>
  );
};

export default SearchFiltersButtonContent;
